import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, MapPin, BookOpen, Search } from 'lucide-react';
import axios from 'axios';

const ClassSchedule = () => {
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    section_id: '' 
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Generate dynamic time slots from actual class times
  const generateTimeSlots = () => {
    const filteredClasses = getFilteredClasses();
    if (filteredClasses.length === 0) {
      // Default time slots if no classes
      return ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
    }
    
    // Extract unique start hours from classes
    const hours = new Set();
    filteredClasses.forEach(cls => {
      const hour = cls.start_time.split(':')[0];
      hours.add(hour);
    });
    
    // Sort and format
    return Array.from(hours)
      .sort()
      .map(hour => `${hour}:00`);
  };

  useEffect(() => {
    fetchClasses();
    fetchCourses();
    fetchSemesters();
    fetchSections();
    fetchSubjects();
    fetchStaff();
  }, []);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/routine/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  // Filter semesters based on selected course
  const getFilteredSemesters = () => {
    if (!filters.course_id) return [];
    return semesters.filter(sem => sem.course_id === filters.course_id);
  };

  // Handle course change
  const handleCourseChange = (courseId) => {
    setFilters({ 
      ...filters, 
      course_id: courseId,
      semester_id: '' // Reset semester when course changes
    });
  };

  // Get filtered classes based on search criteria
  const getFilteredClasses = () => {
    return classes.filter(cls => {
      if (filters.course_id && cls.course_id !== filters.course_id) return false;
      if (filters.semester_id && cls.semester_id !== filters.semester_id) return false;
      if (filters.section_id && cls.section_id !== filters.section_id) return false;
      return cls.is_active; // Only show active classes
    });
  };

  // Get class for specific day and time slot
  const getClassForSlot = (day, timeSlot) => {
    const filteredClasses = getFilteredClasses();
    return filteredClasses.filter(cls => {
      if (cls.day !== day) return false;
      
      // Get the hour from class start time (e.g., "09:30" -> "09")
      const classStartTime = cls.start_time.substring(0, 5); // Get HH:MM
      const classHour = classStartTime.split(':')[0]; // Get hour
      const slotHour = timeSlot.split(':')[0]; // Get hour from slot
      
      // Match if class starts in this hour
      return classHour === slotHour;
    });
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'N/A';
  };

  const getTeacherName = (teacherId) => {
    const teacher = staff.find(t => t.id === teacherId);
    return teacher ? (teacher.full_name || teacher.name || 'N/A') : 'N/A';
  };

  const getSectionName = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section ? section.name : 'N/A';
  };

  const filteredClasses = getFilteredClasses();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Class Schedule</h1>
          <p className="text-sm text-gray-500 mt-1">View weekly class timetable</p>
        </div>
        <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
          <Calendar className="h-5 w-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            {filteredClasses.length} Classes Scheduled
          </span>
        </div>
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <Search className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Search Schedule</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Course Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id}
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          {/* Semester Dropdown (Connected to Course) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Semester
              {filters.course_id && getFilteredSemesters().length === 0 && (
                <span className="text-xs text-orange-600 ml-2">(No semesters)</span>
              )}
            </label>
            <select
              disabled={!filters.course_id}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                !filters.course_id ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              value={filters.semester_id}
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
            >
              <option value="">{!filters.course_id ? 'Select Course First' : 'All Semesters'}</option>
              {getFilteredSemesters().map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>

          {/* Section Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.section_id}
              onChange={(e) => setFilters({ ...filters, section_id: e.target.value })}
            >
              <option value="">All Sections</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
          </div>

          {/* Search Button */}
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ course_id: '', semester_id: '', section_id: '' })}
              className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Weekly Timetable */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">Loading class schedule...</p>
        </div>
      ) : filteredClasses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No classes found</p>
          <p className="text-sm text-gray-400 mt-2">
            {filters.course_id || filters.semester_id || filters.section_id
              ? 'Try adjusting your filters or add classes in Manage Classes'
              : 'Select filters to view class schedule or add classes in Manage Classes'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-blue-700">
                <th className="px-4 py-3 text-left text-sm font-medium text-white border border-blue-500">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Time
                  </div>
                </th>
                {daysOfWeek.map(day => (
                  <th key={day} className="px-4 py-3 text-center text-sm font-medium text-white border border-blue-500">
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {generateTimeSlots().map((timeSlot) => (
                <tr key={timeSlot} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border bg-gray-50">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      {timeSlot}
                    </div>
                  </td>
                  {daysOfWeek.map(day => {
                    const dayClasses = getClassForSlot(day, timeSlot);
                    return (
                      <td key={`${day}-${timeSlot}`} className="px-2 py-2 border align-top">
                        {dayClasses.length > 0 ? (
                          <div className="space-y-2">
                            {dayClasses.map((cls, index) => (
                              <div key={index} className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200 shadow-sm">
                                <div className="flex items-start gap-2 mb-2">
                                  <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                                  <div className="flex-1">
                                    <div className="font-semibold text-blue-900 text-sm">
                                      {getSubjectName(cls.subject_id)}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      {cls.start_time} - {cls.end_time}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-700 mt-2">
                                  <User className="h-3 w-3 text-gray-500" />
                                  <span className="font-medium">{getTeacherName(cls.teacher_id)}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                                  <MapPin className="h-3 w-3 text-gray-500" />
                                  <span>Section: {getSectionName(cls.section_id)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-gray-300 text-xs py-4">
                            —
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClassSchedule;
